import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  DecodedToken,
  TokenUsuario,
  Usuario,
  UsuarioAutenticado,
} from 'src/app/core/models/auth/usuario';
import { BehaviorSubject, Observable, catchError, throwError, map, of } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@env/environment';
import { JwtHelperService } from '@auth0/angular-jwt';
import { encrypt } from '@core/utils/EncryptUtil';

const jwtHelper = new JwtHelperService();
const AUTH_TOKEN_STORAGE_KEY = 'soberania_auth_token';

type AuthProfileResponse = {
  _id?: string;
  id?: string;
  email?: string;
  dni?: string;
  firstName?: string;
  lastName?: string;
  roles?: Array<{ name?: string; internalId?: number; descNombre?: string } | string>;
  tienda?: {
    _id?: string;
    id?: string;
    name?: string;
    nombre?: string;
    direccion?: string;
    address?: string;
    internalId?: number;
    descNombre?: string;
  } | null;
  sistema?: Array<{ _id?: string; rif?: string; ruc?: string }>;
};

type LegacyUsuario = {
  id: string;
  dni: string;
  email: string;
  nombre: string;
  rolApp: string;
  idRolApp: number;
};

type LegacySistema = {
  _id: string;
  rif: string;
  ruc: string;
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  URL_API = `${environment.API_URL}`;
  private http = inject(HttpClient);
  private logueado = new BehaviorSubject<boolean>(false);
  private rol = new BehaviorSubject<string[] | null>(null);
  private idUser = new BehaviorSubject<string | null>(null);
  private userName = new BehaviorSubject<string | null>(null);
  private dni = new BehaviorSubject<string | null>(null);
  private userToken = new BehaviorSubject<string | null>(null);
  private authChecked = new BehaviorSubject<boolean>(false);
  private currentUsuario: UsuarioAutenticado | null = null;

  get isLogged$(): Observable<boolean> {
    return this.logueado.asObservable();
  }
  get isRol$(): Observable<string[] | null> {
    return this.rol.asObservable();
  }
  get isUserId$(): Observable<string | null> {
    return this.idUser.asObservable();
  }
  get isUserName$(): Observable<string | null> {
    return this.userName.asObservable();
  }
  get isDNI$(): Observable<string | null> {
    return this.dni.asObservable();
  }
  get isUserToken$(): Observable<string | null> {
    return this.userToken.asObservable();
  }
  get authChecked$(): Observable<boolean> {
    return this.authChecked.asObservable();
  }

  constructor(private router: Router) {
    this.hydrateFromStorage();
    this.checkToken();
  }

  login(data: Usuario): Observable<TokenUsuario> {
    const base = this.URL_API.replace(/\/$/, '');
    const url = `${base}/auth/login`;
    return this.http.post<TokenUsuario>(url, data, { withCredentials: true }).pipe(
      catchError(this.handlerError),
      map((userResp: TokenUsuario) => {
        const decodedToken: DecodedToken | null = jwtHelper.decodeToken(userResp.token);
        this.hydrateFromToken(decodedToken, userResp.token);
        this.logueado.next(true);
        this.idUser.next(decodedToken?._id as string);
        this.userName.next(this.getDisplayName(decodedToken?.firstName, decodedToken?.lastName, decodedToken?.email));
        this.dni.next(decodedToken?.dni as string);
        this.userToken.next(userResp.token);
        this.persistToken(userResp.token);
        return userResp;
      })
    );
  }

  getAccessToken(): string | null {
    return this.userToken.value;
  }

  private checkToken(): void {
    const base = this.URL_API.replace(/\/$/, '');
    this.http.get<AuthProfileResponse>(`${base}/auth/profile`, { withCredentials: true })
      .pipe(
        catchError(() => of(null)),
      )
      .subscribe((profile) => {
        if (profile) {
          this.hydrateFromProfile(profile);
          this.logueado.next(true);
        }
        // Signal that bootstrap auth verification is done (success or no session).
        this.authChecked.next(true);
      });
  }

  logout(): void {
    const base = this.URL_API.replace(/\/$/, '');
    this.http.post(`${base}/auth/logout`, {}, { withCredentials: true })
      .pipe(catchError(() => of(null)))
      .subscribe(() => {
        this.clearSession();
        this.router.navigate(['/']);
      });
  }

  obtenerDatosUsuario(): UsuarioAutenticado {
    return this.currentUsuario || {
      idTienda: '',
      internalId: 0,
      nombreTienda: '',
      direccion: '',
      rol: '',
      descNombre: '',
    };
  }

  getIdUser(): string {
    return this.idUser.value || '';
  }

  private clearSession(): void {
    this.currentUsuario = null;
    this.logueado.next(false);
    this.idUser.next(null);
    this.userName.next(null);
    this.dni.next(null);
    this.userToken.next(null);
    this.removePersistedToken();
    this.clearLegacyStorage();
  }

  private hydrateFromToken(decodedToken: DecodedToken | null, token: string): void {
    if (!decodedToken) {
      return;
    }

    const role = decodedToken.roles?.[0];
    const tienda: any = Array.isArray((decodedToken as any)?.tienda)
      ? (decodedToken as any).tienda[0]
      : (decodedToken as any)?.tienda;

    this.currentUsuario = {
      idTienda: String(tienda?._id || tienda?.id || ''),
      internalId: Number(role?.internalId || 0),
      nombreTienda: String(tienda?.name || tienda?.nombre || ''),
      direccion: String(tienda?.direccion || tienda?.address || ''),
      rol: String(role?.name || ''),
      descNombre: String(role?.descNombre || role?.name || ''),
    };
    this.userToken.next(token);
    this.syncLegacyStorage(
      {
        id: String(decodedToken._id || ''),
        dni: String(decodedToken.dni || ''),
        email: String(decodedToken.email || ''),
        firstName: String(decodedToken.firstName || ''),
        lastName: String(decodedToken.lastName || ''),
        roleName: String(role?.name || ''),
        roleInternalId: Number(role?.internalId || 0),
      },
      this.getLegacySistemaFromToken(decodedToken)
    );
  }

  private hydrateFromProfile(profile: AuthProfileResponse): void {
    const role = Array.isArray(profile.roles) ? profile.roles[0] : undefined;
    const tienda = profile.tienda;

    this.currentUsuario = {
      idTienda: String(tienda?._id || tienda?.id || ''),
      internalId: Number(typeof role === 'string' ? 0 : role?.internalId || tienda?.internalId || 0),
      nombreTienda: String(tienda?.name || tienda?.nombre || ''),
      direccion: String(tienda?.direccion || tienda?.address || ''),
      rol: String(typeof role === 'string' ? role : role?.name || ''),
      descNombre: String(typeof role === 'string' ? role : role?.descNombre || role?.name || ''),
    };
    this.idUser.next(String(profile._id || profile.id || ''));
    this.userName.next(this.getDisplayName(profile.firstName, profile.lastName, profile.email));
    this.dni.next(String(profile.dni || ''));
    this.syncLegacyStorage(
      {
        id: String(profile._id || profile.id || ''),
        dni: String(profile.dni || ''),
        email: String(profile.email || ''),
        firstName: String(profile.firstName || ''),
        lastName: String(profile.lastName || ''),
        roleName: String(typeof role === 'string' ? role : role?.name || ''),
        roleInternalId: Number(typeof role === 'string' ? 0 : role?.internalId || 0),
      },
      this.getLegacySistemaFromProfile(profile)
    );
  }

  private hydrateFromStorage(): void {
    try {
      const storedToken = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
      if (!storedToken || jwtHelper.isTokenExpired(storedToken)) {
        this.removePersistedToken();
        return;
      }

      const decodedToken: DecodedToken | null = jwtHelper.decodeToken(storedToken);
      this.hydrateFromToken(decodedToken, storedToken);
      this.logueado.next(true);
      this.idUser.next(decodedToken?._id as string);
      this.userName.next(this.getDisplayName(decodedToken?.firstName, decodedToken?.lastName, decodedToken?.email));
      this.dni.next(decodedToken?.dni as string);
      this.userToken.next(storedToken);
    } catch {
      this.removePersistedToken();
    }
  }

  private persistToken(token: string): void {
    try {
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    } catch {
      // Ignore storage restrictions and rely on cookie/session memory.
    }
  }

  private removePersistedToken(): void {
    try {
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    } catch {
      // Ignore storage restrictions.
    }
  }

  private getDisplayName(firstName?: string | null, lastName?: string | null, email?: string | null): string {
    const fullName = `${firstName || ''} ${lastName || ''}`.trim();
    if (fullName) {
      return fullName;
    }
    return String(email || '');
  }

  private getLegacySistemaFromToken(decodedToken: DecodedToken): LegacySistema | null {
    const sistema = Array.isArray(decodedToken.sistema) ? decodedToken.sistema[0] : undefined;
    if (!sistema) {
      return null;
    }

    return {
      _id: String(sistema._id || ''),
      rif: String(sistema.rif || ''),
      ruc: String(sistema.ruc || ''),
    };
  }

  private getLegacySistemaFromProfile(profile: AuthProfileResponse): LegacySistema | null {
    const sistema = Array.isArray(profile.sistema) ? profile.sistema[0] : undefined;
    if (!sistema) {
      return null;
    }

    return {
      _id: String(sistema._id || ''),
      rif: String(sistema.rif || ''),
      ruc: String(sistema.ruc || ''),
    };
  }

  private syncLegacyStorage(
    user: {
      id: string;
      dni: string;
      email: string;
      firstName: string;
      lastName: string;
      roleName: string;
      roleInternalId: number;
    },
    sistema: LegacySistema | null
  ): void {
    const legacyUsuario: LegacyUsuario = {
      id: user.id,
      dni: user.dni,
      email: user.email,
      nombre: this.getDisplayName(user.firstName, user.lastName, user.email),
      rolApp: user.roleName,
      idRolApp: user.roleInternalId,
    };

    try {
      localStorage.setItem('usuarioGarantia', encrypt(JSON.stringify(legacyUsuario)));
      if (sistema) {
        localStorage.setItem('sistemaGarantia', encrypt(JSON.stringify(sistema)));
      }
    } catch {
      // Ignore storage restrictions and keep in-memory session values.
    }
  }

  private clearLegacyStorage(): void {
    try {
      localStorage.removeItem('usuarioGarantia');
      localStorage.removeItem('sistemaGarantia');
      localStorage.removeItem('tiendaGarantia');
    } catch {
      // Ignore storage restrictions.
    }
  }

  handlerError(error: HttpErrorResponse): Observable<never> {
    return throwError(() => error);
  }
}
